import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../store"
import { resolveDefaultSessionId } from "../../../../utils/sessionDefaults"

export interface Session {
  _id: string
  title: string
  startDate: string
  endDate: string
  isActive: boolean
  openForRegistration?: boolean
  isDeleted?: boolean
  deletedAt?: string
  deletedBy?: string
  createdAt?: string
  updatedAt?: string
}

interface SessionsState {
  sessions: Session[]
  currentSession: Session | null
}

const initialState: SessionsState = {
  sessions: [],
  currentSession: null,
}

// Persist the user's session choice across reloads, mirroring branchesSlice's
// CURRENT_BRANCH_KEY pattern — without this, every page refresh silently
// discarded the selection.
const CURRENT_SESSION_KEY = "currentSessionId"
const IMPERSONATION_TOKEN_KEY = "impersonationToken"
const activeStorage = () => {
  try {
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
      ? sessionStorage
      : localStorage
  } catch {
    return localStorage
  }
}

const persistSession = (s: Session | null) => {
  try {
    if (s) activeStorage().setItem(CURRENT_SESSION_KEY, s._id)
    else activeStorage().removeItem(CURRENT_SESSION_KEY)
  } catch {
    /* ignore */
  }
}

const sessionsSlice = createSlice({
  name: "sessions",
  initialState,
  reducers: {
    setSessions: (state, action: PayloadAction<Session[]>) => {
      state.sessions = action.payload
      // Restore the previously-selected session after a reload. Only when
      // nothing is selected yet — never clobber a choice made this session.
      if (!state.currentSession) {
        try {
          const savedId = activeStorage().getItem(CURRENT_SESSION_KEY)
          const match = savedId && state.sessions.find(s => s._id === savedId)
          if (match && match._id !== "6894fd2d97c4f80419122c1f") {
            state.currentSession = match
            return
          }
        } catch {
          /* ignore */
        }
        const defaultId = resolveDefaultSessionId(state.sessions)
        state.currentSession = state.sessions.find(s => s._id === defaultId) || null
      }
    },
    setCurrentSession: (state, action: PayloadAction<Session | null>) => {
      state.currentSession = action.payload
      persistSession(action.payload)
    },
  },
})

export const { setSessions, setCurrentSession } = sessionsSlice.actions

export const selectSessions = (state: RootState) => state.sessions.sessions

export const selectCurrentSession = (state: RootState) => state.sessions.currentSession

export default sessionsSlice.reducer
